import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import React, { useMemo } from 'react';
import { RequestService } from '../../api/request-service';
import { AuthenticationService } from '../../auth/auth-service';
import { ForgotPasswordService } from '../../auth/forgot-password-service';
import { EnvironmentService } from '../../environment/environment-service';
import { IntercomStore } from '../../intercom/intercom-store';
import { LanguageStore } from '../../localization/language-store';
import { BaseTracker } from '../analytics/trackers/baseTracker';
import { UserTracker } from '../analytics/trackers/userTracker';
import { LocalStorageDomainService } from '../api/domain/service';
import { LocalStorageLanguageService } from '../api/language/service';
import { LocalStorageExecutorRoleService } from '../api/role/service';
import { LocalStorageUserService } from '../api/user/local-storage-service';
import { UnauthErrorTracker } from '../common/components/errors/trackers/UnauthErrorTracker';
import {
  AuthenticationServiceContext,
  BaseTrackerContext,
  CurrentUserFromLocalStorageContext,
  DomainFromLocalStorageContext,
  EnvironmentServiceContext,
  ForgotPasswordServiceContext,
  IntercomStoreContext,
  LanguageStoreContext,
  LocaleContext,
  LocalStorageCurrentUserServiceContext,
  LocalStorageDomainServiceContext,
  LocalStorageExecutorRoleServiceContext,
  MixpanelContext,
  UnauthErrorTrackerContext,
  UserTrackerContext,
} from './contexts';
import { initFavicon } from './favicon';
import { useMemoWithDeps } from './hooks';
import { Mixpanel } from './mixpanel';
import { initSentry } from './sentry';

const DebugPrefix = 'UnauthenticatedContextProvider';
export const UnauthenticatedContextProvider = ({
  children,
}: {
  children: React.ReactNode;
}) => {
  console.log2('UnauthenticatedContextProvider start');
  const reactQueryClient = useMemo(
    () =>
      new QueryClient({
        defaultOptions: {
          queries: {
            staleTime: Infinity,
            retry: false,
          },
        },
      }),
    []
  );

  const environmentService = useMemo(() => new EnvironmentService(), []);
  const mixpanel = useMemoWithDeps(
    `${DebugPrefix} - mixpanel`,
    () => new Mixpanel(environmentService),
    [],
    [environmentService]
  );
  const baseTracker = useMemoWithDeps(
    `${DebugPrefix} - baseTracker`,
    () => new BaseTracker(mixpanel),
    [],
    [mixpanel]
  );
  const userTracker = useMemoWithDeps(
    `${DebugPrefix} - userTracker`,
    () => new UserTracker(baseTracker),
    [],
    [baseTracker]
  );

  // TODO: rewrite these
  const localStorageDomainService = useMemoWithDeps(
    `${DebugPrefix} - localStorageDomainService`,
    () => LocalStorageDomainService,
    []
  );
  const domainFromLocalStorage = useMemoWithDeps(
    `${DebugPrefix} - domainFromLocalStorage`,
    () => localStorageDomainService.get(),
    []
  );

  const localStorageCurrentUserService = useMemoWithDeps(
    `${DebugPrefix} - localStorageCurrentUserService`,
    () => LocalStorageUserService,
    []
  );

  const localStorageExecutorRoleService = useMemoWithDeps(
    `${DebugPrefix} - localStorageExecutorRoleService`,
    () => LocalStorageExecutorRoleService,
    []
  );

  const localStorageLanguageService = useMemoWithDeps(
    `${DebugPrefix} - localStorageLanguageService`,
    () => new LocalStorageLanguageService(),
    []
  );
  const selectedLanguageFromLocalStorage = useMemoWithDeps(
    `${DebugPrefix} - selectedLanguageFromLocalStorage`,
    () => localStorageLanguageService.get(),
    [],
    [localStorageLanguageService]
  );

  const localStorageUserService = useMemoWithDeps(
    `${DebugPrefix} - localStorageUserService`,
    () => LocalStorageUserService,
    []
  );
  const userFromLocalStorage = useMemoWithDeps(
    `${DebugPrefix} - userFromLocalStorage`,
    () => localStorageUserService.get(),
    []
  );
  // TODO: fix this when we stop reloading the whole page on language switch
  const languageStore = useMemoWithDeps(
    `${DebugPrefix} - languageStore`,
    () => new LanguageStore(selectedLanguageFromLocalStorage),
    [selectedLanguageFromLocalStorage.id]
  );
  const intercomStore = useMemoWithDeps(
    `${DebugPrefix} - intercomStore`,
    () => new IntercomStore(languageStore, environmentService),
    [],
    [languageStore, environmentService]
  );
  const unauthErrorTracker = useMemoWithDeps(
    `${DebugPrefix} - unauthErrorTracker`,
    () =>
      new UnauthErrorTracker(
        environmentService,
        localStorageUserService,
        localStorageDomainService,
        localStorageExecutorRoleService
      ),
    [],
    [
      environmentService,
      localStorageUserService,
      localStorageDomainService,
      localStorageExecutorRoleService,
    ]
  );
  const authService = useMemoWithDeps(
    `${DebugPrefix} - authService`,
    () =>
      new AuthenticationService(
        environmentService,
        unauthErrorTracker,
        userTracker
      ),
    [],
    [environmentService, unauthErrorTracker, userTracker]
  );

  const requestService = useMemoWithDeps(
    `${DebugPrefix} - requestService`,
    () => new RequestService(unauthErrorTracker, environmentService),
    [],
    [environmentService, unauthErrorTracker]
  );
  const forgotPasswordService = useMemoWithDeps(
    `${DebugPrefix} - forgotPasswordService`,
    () => new ForgotPasswordService(requestService, environmentService),
    [],
    [environmentService, requestService]
  );

  const environment = environmentService.getEnvironment();

  initSentry(environment);
  initFavicon(environment);

  console.log2('UnauthenticatedContextProvider end');
  return (
    <MixpanelContext.Provider value={mixpanel}>
      <ForgotPasswordServiceContext.Provider value={forgotPasswordService}>
        <UnauthErrorTrackerContext.Provider value={unauthErrorTracker}>
          <EnvironmentServiceContext.Provider value={environmentService}>
            <DomainFromLocalStorageContext.Provider
              value={domainFromLocalStorage}
            >
              <CurrentUserFromLocalStorageContext.Provider
                value={userFromLocalStorage}
              >
                <LocalStorageCurrentUserServiceContext.Provider
                  value={localStorageCurrentUserService}
                >
                  <LocalStorageDomainServiceContext.Provider
                    value={localStorageDomainService}
                  >
                    <LanguageStoreContext.Provider value={languageStore}>
                      <IntercomStoreContext.Provider value={intercomStore}>
                        <LocaleContext.Provider
                          value={{
                            selected: selectedLanguageFromLocalStorage.id,
                            object: selectedLanguageFromLocalStorage,
                          }}
                        >
                          <QueryClientProvider client={reactQueryClient}>
                            <AuthenticationServiceContext.Provider
                              value={authService}
                            >
                              <BaseTrackerContext.Provider value={baseTracker}>
                                <UserTrackerContext.Provider
                                  value={userTracker}
                                >
                                  <LocalStorageExecutorRoleServiceContext.Provider
                                    value={localStorageExecutorRoleService}
                                  >
                                    {children}
                                  </LocalStorageExecutorRoleServiceContext.Provider>
                                </UserTrackerContext.Provider>
                              </BaseTrackerContext.Provider>
                            </AuthenticationServiceContext.Provider>
                          </QueryClientProvider>
                        </LocaleContext.Provider>
                      </IntercomStoreContext.Provider>
                    </LanguageStoreContext.Provider>
                  </LocalStorageDomainServiceContext.Provider>
                </LocalStorageCurrentUserServiceContext.Provider>
              </CurrentUserFromLocalStorageContext.Provider>
            </DomainFromLocalStorageContext.Provider>
          </EnvironmentServiceContext.Provider>
        </UnauthErrorTrackerContext.Provider>
      </ForgotPasswordServiceContext.Provider>
    </MixpanelContext.Provider>
  );
};
